import { useMutation } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { apiHeader, SERVER_URL } from '../config'
import apiClient from '../services/apiClient'
import { TrainingSessionFormData } from './useTrainingSessionForm'

type CreateTrainingSessionData = Omit<TrainingSessionFormData, 'id' | 'createdAt' | 'updatedAt'>
type UpdateTrainingSessionData = Omit<TrainingSessionFormData, 'coachId' | 'createdAt' | 'updatedAt'> & {
    id: number
}

interface UseTrainingSessionMutationsProps {
    user: { coachData: { id: number } } | null
    onSuccess: () => void
    onSettled: () => void
}

export const useTrainingSessionMutations = ({ user, onSuccess, onSettled }: UseTrainingSessionMutationsProps) => {
    const { t } = useTranslation()

    const createMutation = useMutation<unknown, AxiosError<{ message?: string }>, CreateTrainingSessionData>({
        mutationFn: async (data) => {
            if (!user) throw new Error('User not authenticated')
            const response = await apiClient.post<unknown>('/training-sessions', data)

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to create training session')
            }

            return response.data
        },
        onSuccess: () => {
            toast.success(t('coach.trainingSessions.createdSuccess'))
            onSuccess()
        },
        onError: (error) => {
            console.error('Error creating training session:', error)
            const errorMessage = error.response?.data?.message ?? (error.message || t('common.errorOccurred'))
            toast.error(typeof errorMessage === 'string' ? errorMessage : t('common.errorOccurred'))
        },
        onSettled,
    })

    const updateMutation = useMutation<unknown, AxiosError<{ message?: string }>, UpdateTrainingSessionData>({
        mutationFn: async (data) => {
            if (!user) throw new Error('User not authenticated')
            const response = await apiClient.patch<unknown>(`/training-sessions/${data.id}`, data)

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to update training session')
            }

            return response.data
        },
        onSuccess: () => {
            toast.success(t('coach.trainingSessions.updatedSuccess'))
            onSuccess()
        },
        onError: (error) => {
            console.error('Error updating training session:', error)
            const errorMessage = error.response?.data?.message ?? (error.message || t('common.errorOccurred'))
            toast.error(typeof errorMessage === 'string' ? errorMessage : t('common.errorOccurred'))
        },
        onSettled,
    })

    const submitTrainingSession = async (formData: TrainingSessionFormData, sessionId?: number) => {
        const submissionData = {
            ...formData,
            order: +formData.order,
            groupId: formData.groupId ? +formData.groupId : undefined,
            steps: formData.steps,
        }

        if (sessionId) {
            const updateData: UpdateTrainingSessionData = {
                ...submissionData,
                id: sessionId,
                description: submissionData.description !== undefined ? submissionData.description.trim() : undefined,
                steps: submissionData.steps,
            }
            await updateMutation.mutateAsync(updateData)
        } else {
            const cleanData: CreateTrainingSessionData = {
                ...submissionData,
                description: submissionData.description !== undefined ? submissionData.description.trim() : undefined,
                steps: submissionData.steps,
            }
            await createMutation.mutateAsync(cleanData)
        }
    }

    return {
        createMutation,
        updateMutation,
        submitTrainingSession,
        isLoading: createMutation.isPending || updateMutation.isPending,
    }
}
